// ============================================================
// TMR Portal Admin — a name field that suggests, registers and corrects a Party inline
// ============================================================
import { useEffect, useMemo, useRef, useState } from 'react';
import { Icons, StrokeIcon } from '../../lib/icons';
import { maskCPF } from '../../lib/format';
import { PERMISSIONS, usePermission } from '../../data/PermissionsProvider';
import { usePartySearch } from '../../data/parties/queries';
import type { PartyOption } from '../../data/parties/contract';
import type { PartyFormValues } from '../../data/parties/form';
import { PartyFormModal } from '../../components/PartyFormModal';
import { useDebounced } from '../../useDebounced';

/** Props for {@link PartyNameField}. */
export interface PartyNameFieldProps {
  label: string;
  required?: boolean;
  /** The name the operator is typing — also the term the suggestions search by. */
  value: string;
  onChange: (v: string) => void;
  error?: string;
  /** Fills the whole block from a Party — a chosen one, a newly registered one, or a corrected one. */
  onPick: (party: PartyOption) => void;
  /** The block's current values, used as the starting point of a registration. */
  buildDraft: () => PartyFormValues;
  /**
   * The Party the block currently points at, empty when it holds typed details (10 B8.4).
   *
   * It decides two things: what the payload carries (`partyId` versus the details), and whether the
   * operator can open that person's record to correct it. The field stays editable either way, and the
   * first edit is what drops the reference.
   */
  partyId?: string;
}

/**
 * The Nome field of a contact block, turned into a person typeahead (10 B5b).
 *
 * As the operator types, it suggests matching Parties — by name or CPF, since the backend search covers
 * both — so a person already in the directory is filled in with one click instead of retyped. When nobody
 * matches, the same dropdown **registers** one, so onboarding builds the directory instead of leaving
 * every contact a one-off (usability ask, 2026-07-23).
 *
 * Registering opens the full person form rather than posting what the block happens to hold. It used to
 * post directly, and the row sits in the *name* dropdown — reached right after typing a name and before
 * any other field — so the directory filled up with people who had nothing but a name. Worse, the block
 * was not pointed at the person it had just created, so submitting the Empresa filed a *second*, complete
 * one. Both are fixed by the same change: the operator sees the whole record before it is saved, and the
 * block adopts what was saved (09 §10, F10).
 *
 * The two halves are gated separately since B8.4, because they are separate powers: **suggesting** needs
 * `parties.read`, **registering and correcting** need `parties.manage`. Without either the field is a
 * plain input and manual entry carries on (Regra 5) — which is exactly why the registration still accepts
 * typed details: an operator who can onboard an Empresa but not read the directory has to be able to
 * finish (10 B8.4).
 */
export function PartyNameField({ label, required, value, onChange, error, onPick, buildDraft, partyId }: PartyNameFieldProps) {
  const canReadParties = usePermission(PERMISSIONS.partiesRead);
  const canManageParties = usePermission(PERMISSIONS.partiesManage);

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [saved, setSaved] = useState<'created' | 'updated' | null>(null);
  const [form, setForm] = useState<'new' | 'edit' | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const debounced = useDebounced(value);
  const suggestions = usePartySearch(canReadParties ? debounced : '');

  const term = value.trim();
  const canSearch = term.length >= 2;
  const picked = Boolean(partyId);
  const rows = useMemo(() => suggestions.data ?? [], [suggestions.data]);
  // The register row sits after the suggestions, so keyboard navigation reaches it last — and only exists
  // with `parties.manage`. Without it, the last row the keyboard can reach is the last suggestion.
  const createIndex = rows.length;
  const lastIndex = canManageParties ? createIndex : rows.length - 1;

  // Close when the focus or a click leaves the field and its dropdown.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // A fresh keystroke reopens the list and drops the just-saved confirmation.
  useEffect(() => {
    setActive(0);
  }, [debounced]);

  if (!canReadParties) {
    return <PlainField label={label} required={required} value={value} onChange={onChange} error={error} />;
  }

  const pick = (party: PartyOption) => {
    onPick(party);
    setOpen(false);
  };

  const register = () => {
    if (!canManageParties) return;
    setOpen(false);
    setForm('new');
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, lastIndex));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && open) {
      e.preventDefault();
      if (canManageParties && active === createIndex) register();
      else if (rows[active]) pick(rows[active]);
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <label className="pa-form-label">
        {label} {required && <span style={{ color: 'var(--danger)' }}>*</span>}
      </label>
      <div style={{ position: 'relative' }}>
        <input
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
            setSaved(null);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Nome ou CPF — busca no cadastro de pessoas"
          className="pa-input"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          style={{
            width: '100%',
            padding: '9px 36px 9px 12px',
            fontFamily: 'var(--font-sans)',
            fontSize: 14,
            border: `1px solid ${error ? 'var(--danger)' : 'var(--border-default)'}`,
            borderRadius: 'var(--radius-sm)',
            background: 'var(--cream-50)',
            color: 'var(--ink-900)',
            outline: 'none',
          }}
        />
        <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--stone-400)', display: 'flex' }}>
          <StrokeIcon d={Icons.search} size={15} />
        </span>
      </div>

      {error && <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 5 }}>{error}</div>}

      {picked && !error && !saved && (
        <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 5, display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
          <StrokeIcon d={Icons.check} size={13} /> Pessoa do cadastro — editar qualquer campo desfaz o vínculo.
          {canManageParties && (
            // Correcting the person is the other half of the fix: editing these fields does not rename the
            // Party — the backend matches by CPF and returns the same person untouched, so the change
            // reverts on the next read (09 §10). This is where it is corrected for real.
            <button type="button" onClick={() => setForm('edit')} style={linkButtonStyle}>
              Corrigir no cadastro de pessoas
            </button>
          )}
        </div>
      )}
      {saved && !error && (
        <div style={{ fontSize: 12, color: 'var(--forest-700)', marginTop: 5, display: 'flex', alignItems: 'center', gap: 5 }}>
          <StrokeIcon d={Icons.check} size={13} />
          {saved === 'created'
            ? 'Pessoa cadastrada — o bloco passou a apontar para ela.'
            : 'Cadastro da pessoa atualizado.'}
        </div>
      )}

      {open && canSearch && (
        <div
          style={{ position: 'absolute', zIndex: 40, top: '100%', left: 0, right: 0, marginTop: 4, background: 'var(--cream-50)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', boxShadow: 'var(--shadow-lg)', overflow: 'hidden', maxHeight: 280, overflowY: 'auto' }}
        >
          {suggestions.isPending ? (
            <div style={{ padding: '12px 14px', fontSize: 12.5, color: 'var(--fg-muted)' }}>Buscando…</div>
          ) : suggestions.isError ? (
            <div style={{ padding: '12px 14px', fontSize: 12.5, color: 'var(--danger)' }}>Falha ao buscar pessoas.</div>
          ) : (
            rows.map((party, index) => (
              <button
                key={party.id}
                type="button"
                onMouseEnter={() => setActive(index)}
                onClick={() => pick(party)}
                style={rowStyle(active === index)}
              >
                <div style={{ fontSize: 14, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{party.name}</div>
                <div style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>
                  {[party.cpf ? maskCPF(party.cpf) : null, party.email].filter(Boolean).join(' · ') || 'sem CPF ou e-mail'}
                </div>
              </button>
            ))
          )}

          {!suggestions.isPending && rows.length === 0 && (
            <div style={{ padding: '10px 14px', fontSize: 12.5, color: 'var(--fg-muted)' }}>Ninguém encontrado com esse termo.</div>
          )}

          {canManageParties && (
            <button
              type="button"
              onMouseEnter={() => setActive(createIndex)}
              onClick={register}
              style={{ ...rowStyle(active === createIndex), borderTop: rows.length ? '1px solid var(--border-default)' : undefined, color: 'var(--forest-700)', fontWeight: 500 }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13 }}>
                <StrokeIcon d={Icons.plus} size={15} />
                Cadastrar <strong>{term || 'nova pessoa'}</strong> no cadastro de pessoas
              </span>
            </button>
          )}
        </div>
      )}

      {form === 'new' && (
        <PartyFormModal
          initial={buildDraft()}
          hint="Confira os dados antes de salvar: é assim que a pessoa fica no cadastro, e o bloco passa a apontar para ela."
          onClose={() => setForm(null)}
          onSaved={(party) => {
            onPick(party);
            setSaved('created');
          }}
        />
      )}
      {form === 'edit' && partyId && (
        <PartyFormModal
          partyId={partyId}
          hint="A correção vale para o cadastro de pessoas — e este bloco é atualizado junto."
          onClose={() => setForm(null)}
          onSaved={(party) => {
            onPick(party);
            setSaved('updated');
          }}
        />
      )}
    </div>
  );
}

/** One dropdown row's style, highlighted when it is the active keyboard/hover target. */
function rowStyle(activeRow: boolean): React.CSSProperties {
  return {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '9px 14px',
    border: 0,
    background: activeRow ? 'var(--cream-100)' : 'transparent',
    cursor: 'pointer',
  };
}

/** A textual action inside a hint line — same weight as the text it sits next to. */
const linkButtonStyle: React.CSSProperties = {
  border: 0,
  background: 'transparent',
  padding: 0,
  fontFamily: 'var(--font-sans)',
  fontSize: 12,
  color: 'var(--forest-700)',
  textDecoration: 'underline',
  cursor: 'pointer',
};

/** The field the way {@link TextField} renders it, used when the operator cannot touch the directory. */
function PlainField({ label, required, value, onChange, error }: Pick<PartyNameFieldProps, 'label' | 'required' | 'value' | 'onChange' | 'error'>) {
  return (
    <div>
      <label className="pa-form-label">
        {label} {required && <span style={{ color: 'var(--danger)' }}>*</span>}
      </label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pa-input"
        style={{ width: '100%', padding: '9px 12px', fontFamily: 'var(--font-sans)', fontSize: 14, border: `1px solid ${error ? 'var(--danger)' : 'var(--border-default)'}`, borderRadius: 'var(--radius-sm)', background: 'var(--cream-50)', color: 'var(--ink-900)', outline: 'none' }}
      />
      {error && <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 5 }}>{error}</div>}
    </div>
  );
}
